import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";

const DATA_FILE_PATH = "/uploads/data.json";
const PLAYABLE_THUMBNAIL_PATH = "/uploads/PlayableThumb/";
const BRAND_ASSET_PATH = "/uploads/Brand/";
const BACKGROUND_IMAGE_FILENAME = "BG.png";

const CARD_WIDTH = 150;
const GAP_SIZE = 40;

const slugify = (text) => {
    return text
        .toLowerCase()
        .trim()
        .replace(/&/g, 'and')
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
};

const unslugifyAndCapitalize = (slug) => {
    const words = slug.split('-');
    const capitalized = words.map(word => {
        if (word === 'and') return '&';
        return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');

    if (capitalized.includes(' & ')) {
        return capitalized.replace(' & ', ' & ');
    }
    return capitalized;
};

export default function BrandPlayablesPage() {
    const { brandSlug } = useParams();
    const [playables, setPlayables] = useState([]);
    const [brandInfo, setBrandInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setIsLoading(true);
        setError(null);

        fetch(DATA_FILE_PATH)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch data: ${response.statusText}`);
                }
                return response.json();
            })
            .then(data => {
                const targetBrand = data.Brands.find(
                    (brand) => slugify(brand.name) === brandSlug
                );

                if (!targetBrand) {
                    setError("Brand not found.");
                    setBrandInfo(null);
                    return;
                }
                setBrandInfo(targetBrand);

                const filteredPlayables = data.Playables.filter(
                    (playable) => playable.brandID === targetBrand.brandID
                );
                setPlayables(filteredPlayables);

            })
            .catch(err => {
                console.error("Error loading data:", err);
                setError("Failed to load brand data.");
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [brandSlug]);

    const handleLogoError = (e, baseFileName) => {
        const extensions = ['', '.webp', '.svg', '.png', '.jpg'];
        const currentSrc = e.target.getAttribute('data-fallback-index') || 0;
        let nextIndex = parseInt(currentSrc, 10) + 1;

        const cleanedBaseName = baseFileName.replace(/\.(webp|svg|png|jpg)$/i, '');

        if (nextIndex < extensions.length) {
            const nextExtension = extensions[nextIndex];
            const newPath = `${BRAND_ASSET_PATH}${cleanedBaseName}${nextExtension}`;

            e.target.setAttribute('data-fallback-index', nextIndex);
            e.target.src = newPath;
        } else {
            e.target.onerror = null;
            e.target.style.display = 'none';
            e.target.parentNode.innerHTML = '<span style="color:red; font-size:12px;">LOGO FAIL</span>';
        }
    };

    if (isLoading) {
        return <p className="text-center p-20 min-h-screen bg-white">Loading playables...</p>;
    }

    if (error || !brandInfo) {
        return <p className="text-center p-20 min-h-screen bg-white text-red-600 text-xl">{error || "Brand data not available."}</p>;
    }

    const fiveCardWidth = `${(5 * CARD_WIDTH) + (4 * GAP_SIZE)}px`;
    const backgroundPath = `${BRAND_ASSET_PATH}${BACKGROUND_IMAGE_FILENAME}`;
    const playableLogoPath = `${BRAND_ASSET_PATH}${brandInfo.playableBanner}`;
    const descriptionText = brandInfo.playableDescription || `Explore the playable advertisements created for ${brandInfo.name}.`;

    return (
        <div className="min-h-screen bg-white">
            <section className="relative w-full overflow-hidden">
                {/* <img
                    className="absolute inset-0 w-full h-full object-cover" 
                    src={backgroundPath}
                    alt={`${brandInfo.name} Background`}
                /> */}

                <div className="absolute inset-0 bg-black opacity-50"></div>

                <div className="relative mx-auto max-w-10xl p-16 px-4 sm:px-6 z-10">
                    <div className="flex flex-col items-center text-center py-10">

                        <img
                            src={playableLogoPath}
                            alt={`${brandInfo.name} Showcase Logo`}
                            style={{ width: '200px' }}
                            className=" max-w-xl h-auto "
                            onError={(e) => handleLogoError(e, brandInfo.playableBanner)}
                        />

                        <p
                            className="
                                mt-6 text-white font-uber max-w-4xl 
                                md:text-[28px] md:leading-[36px] md:font-bold 
                                xl-custom:text-[30px] xl-custom:leading-[38px]
                            "
                        >
                            {descriptionText}
                        </p>
                    </div>
                </div>
            </section>

            <section className="relative w-full overflow-hidden">
                <div className="mx-auto max-w-6xl p-20 px-4 sm:px-6">
                    <div
                        className="mx-auto flex flex-wrap justify-center gap-10"
                        style={{ maxWidth: fiveCardWidth }}
                    >

                        {playables.length > 0 ? (
                            playables.map((playableItem, index) => {
                                const assetPath = `${PLAYABLE_THUMBNAIL_PATH}${playableItem.thumbnail}`;

                                const linkTo = `/playable/${playableItem.id}`;

                                return (
                                    <Link
                                        to={linkTo}
                                        key={index}
                                        className="w-full max-w-[150px] flex-shrink-0"
                                    >
                                        <img
                                            src={assetPath}
                                            alt={playableItem.name}
                                            className="w-full h-auto object-cover rounded-lg shadow-lg"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "/placeholder-image.png";
                                            }}
                                        />
                                    </Link>
                                );
                            })
                        ) : (
                            <p className="text-center text-xl text-gray-500 w-full">
                                No playables found for {brandInfo.name}.
                            </p>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}